import { Button, DatePicker, message, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useState } from 'react';

import PPResizableTable from '../../components/table/PPResizableTable';
import { isHandledByInterceptor } from '../../utils/apiError';
import { getIndexColumn } from '../../utils/getIndexColumn';
import rolesDictionary from '../../config/rolesDictionary';
import ReportFilterBar, { ReportField } from './ReportFilterBar';
import { useCachedState } from './reportCache';
import { findMissingRangeError, getExportUrl, startDownload } from './reportRequest';
import './style.less';

const { RangePicker } = DatePicker;

const ORDER_STATUS_OPTIONS = [
  { label: 'Бүгд', value: 'all' },
  { label: 'Үүсгэсэн', value: 'created' },
  { label: 'Захиалга бүрдсэн', value: 'completed' },
  { label: 'Хүргэлтэнд гарсан', value: 'delivery' },
  { label: 'Бараа хүргэгдсэн', value: 'delivered' },
  { label: 'Цуцалсан', value: 'canceled' },
];

/* --- helpers --- */

const formatDateTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const formatAmount = (value) => (typeof value === 'number' ? value.toFixed(2) : '-');

const formatWeight = (value) => (typeof value === 'number' ? value.toFixed(3) : (value ?? '-'));

const statusLabel = (status) =>
  rolesDictionary.orderStatus.find((role) => role.value === status)?.label || '-';

/* --- columns --- */

const defaultColumns = [
  { title: 'Захиалгын дугаар', dataIndex: 'code', key: 'code', width: 170 },
  {
    title: 'Үүссэн огноо',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 170,
    render: formatDateTime,
  },
  { title: 'Захиалгын төлөв', dataIndex: 'orderStatus', key: 'orderStatus', width: 160 },
  {
    title: 'Дууссан огноо',
    dataIndex: 'orderCompletedDate',
    key: 'orderCompletedDate',
    width: 170,
    render: formatDateTime,
  },
  { title: 'Хэрэглэгчийн нэр', dataIndex: 'customerName', key: 'customerName', width: 170 },
  { title: 'Утасны дугаар', dataIndex: 'customerPhone', key: 'customerPhone', width: 140 },
  { title: 'Хаяг', dataIndex: 'dropoffDetail', key: 'dropoffDetail', width: 220 },
  { title: 'Үүсгэсэн ажилтан', dataIndex: 'requesterName', key: 'requesterName', width: 170 },
  {
    title: 'Барааны төрөл',
    dataIndex: 'numberOfChildOrders',
    key: 'numberOfChildOrders',
    width: 140,
  },
  { title: 'Барааны тоо', dataIndex: 'totalUnit', key: 'totalUnit', width: 120 },
  {
    title: 'Нийт захиалгын дүн',
    dataIndex: 'totalPrice',
    key: 'totalPrice',
    width: 170,
    render: formatAmount,
  },
  { title: 'Нийт жин /кг/', dataIndex: 'totalWeight', key: 'totalWeight', width: 140 },
  {
    title: 'Нийт овор хэмжээ',
    dataIndex: 'totalVolumeV',
    key: 'totalVolumeV',
    width: 160,
    render: formatAmount,
  },
  {
    title: 'Хүргэлтийн үнэ',
    dataIndex: 'totalDeliveryAmount',
    key: 'totalDeliveryAmount',
    width: 150,
    render: formatAmount,
  },
  {
    title: 'Захиалга гүйцэтгэлийн үнэ',
    dataIndex: 'totalExecutionAmount',
    key: 'totalExecutionAmount',
    width: 200,
    render: formatAmount,
  },
  {
    title: 'Нийт үйлчилгээний үнэ',
    dataIndex: 'totalServiceAmount',
    key: 'totalServiceAmount',
    width: 180,
    render: formatAmount,
  },
  { title: 'Захиалга үүсгэсэн', dataIndex: 'requesterEmail', key: 'requesterEmail', width: 200 },
  { title: 'Захиалга төрөл', dataIndex: 'serviceType', key: 'serviceType', width: 150 },
];

/** Хүснэгтэд харуулах хэлбэрт хөрвүүлнэ (эх өгөгдлийг өөрчлөхгүй) */
const normalizeRow = (row) => ({
  ...row,
  orderStatus: statusLabel(row.orderStatus),
  totalWeight: formatWeight(row.totalWeight),
});

const OrderReport = () => {
  // Таб солиод буцаж ирэхэд хайлтын нөхцөл, үр дүн хэвээр үлдэнэ
  const [range, setRange] = useCachedState('order:range', null);
  const [orderStatus, setOrderStatus] = useCachedState('order:status', 'all');
  const [reports, setReports] = useCachedState('order:reports', []);
  const [pagination, setPagination] = useCachedState('order:pagination', {
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const columns = [getIndexColumn(pagination.current, pagination.pageSize), ...defaultColumns];

  const params = {
    startDate: range?.[0] ? range[0].format('YYYY-MM-DD') : '',
    endDate: range?.[1] ? range[1].format('YYYY-MM-DD') : '',
    orderStatus,
  };

  const getReportData = async (page = 1, pageSize = pagination.pageSize) => {
    /**
     * Хугацаагаа бүрэн сонгоогүй үед хайлт явуулахгүй — өмнө нь хоосон огноотой
     * хүсэлт явж, хүснэгтэд өмнөх үр дүн хэвээр үлддэг байв.
     */
    const rangeError = findMissingRangeError(range);
    if (rangeError) {
      message.warning(rangeError);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/v1.0/request/report', { ...params, page, pageSize });

      if (res.data?.statusCode === 200) {
        if (res.data?.result?.totalDocs === 0) {
          message.warning('Тайлан байхгүй байна.');
        }
        setReports((res.data.result.docs || []).map(normalizeRow));
        setPagination({ current: page, pageSize, total: res.data.result.totalDocs ?? 0 });
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Order report error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const rangeError = findMissingRangeError(range);
    if (rangeError) {
      message.warning(rangeError);
      return;
    }

    setExporting(true);
    try {
      const res = await axios.post('/api/v1.0/request/report-excel', params);

      const url = getExportUrl(res);
      // Өгөгдөл байхгүй үед «амжилттай татагдлаа» гэж хэлэхгүй
      if (!url) {
        // Серверийн алдааг interceptor мэдэгдсэн бол «байхгүй» гэж давхардуулахгүй
        if (!isHandledByInterceptor(res.data)) message.warning('Татах тайлан байхгүй байна.');
        return;
      }

      startDownload(url);
      message.success('Excel файл амжилттай татагдлаа.');
    } catch (error) {
      console.error('Order report export error: ', error);
    } finally {
      setExporting(false);
    }
  };

  const handleTableChange = (paginationConfig) =>
    getReportData(paginationConfig.current, paginationConfig.pageSize);

  return (
    <div className="pp_table_wrapper">
      <ReportFilterBar
        onSearch={() => getReportData(1)}
        loading={loading}
        actions={
          <Button
            className="all_buttons"
            icon={<DownloadOutlined />}
            onClick={exportExcel}
            loading={exporting}
          >
            Export
          </Button>
        }
      >
        <ReportField label="Хугацаа сонгоно уу">
          <RangePicker className="range-picker all_buttons" value={range} onChange={setRange} />
        </ReportField>

        <ReportField label="Захиалгын төлөв">
          <Select
            className="select all_buttons"
            value={orderStatus}
            onChange={setOrderStatus}
            options={ORDER_STATUS_OPTIONS}
          />
        </ReportField>
      </ReportFilterBar>

      <PPResizableTable
        rowKey={(record, index) => record?._id ?? index}
        columns={columns}
        dataSource={reports}
        loading={loading}
        size="middle"
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
      />
    </div>
  );
};

export default OrderReport;
